import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Tabs,
  TabList,
  Tab,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  Text,
  Button,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { ArrowForwardIcon, ArrowBackIcon, ArrowDownIcon, ArrowUpIcon } from '@chakra-ui/icons';
import { FiArrowDownRight, FiArrowUpRight } from 'react-icons/fi';
import DatePicker, { DatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled from '@emotion/styled';

import { useAppDispatch, useAppSelector } from '../../../utils/reduxHooks';
import { getBalanceAction } from '../../../modules/wallet/actions';
import { selectBalance } from '../../../modules/wallet/selectors';

// Стилизованный DatePicker
const StyledDatePicker = styled(DatePicker as unknown as React.ComponentType<DatePickerProps>)`
  width: 150px;
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #4a5568;
  background-color: white;
  &:hover {
    border-color: #cbd5e0;
  }
  &:focus {
    border-color: #3182ce;
    box-shadow: 0 0 0 1px #3182ce;
  }
`;

export const TransactionHistory: React.FC = () => {
  const dispatch = useAppDispatch();
  const balance = useAppSelector(selectBalance);
  const [selectedTab, setSelectedTab] = useState<'all' | 'income' | 'expense'>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(getBalanceAction.request());
  }, [dispatch]);

  const itemsPerPage = 10;
  const operations = balance?.operations || [];
  const totalItems = operations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const filteredTransactions = operations.filter(operation => {
    if (selectedTab === 'income' && operation.direction !== 1) return false;
    if (selectedTab === 'expense' && operation.direction !== 0) return false;
    return true;
  });

  const paginatedTransactions = filteredTransactions.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const handleTabChange = (tab: 'all' | 'income' | 'expense') => {
    setSelectedTab(tab);
    setPage(1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handlePreviousPage = () => {
    if (page > 1) setPage(page - 1);
  };

  return (
    <Box maxW="1200px" mx="auto" p={6}>
      <Heading size="lg" mb={4}>
        История списаний и пополнений
      </Heading>

      <Flex justifyContent="space-between" alignItems="flex-start">
        {/* Основной блок с таблицей и фильтрами */}
        <Box flex="1" mr={8}>
          {/* Табы и фильтр по дате на одной линии */}
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Tabs variant="unstyled">
              <TabList>
                <Tab
                  fontWeight="bold"
                  color="gray.500"
                  _selected={{ color: 'black', borderBottom: '2px solid black' }}
                  onClick={() => handleTabChange('all')}
                >
                  Все операции
                </Tab>
                {/* Поменяли местами вкладки "Поступления" и "Списания" */}
                <Tab
                  color="gray.500"
                  fontWeight="bold"
                  _selected={{ color: 'black', borderBottom: '2px solid black' }}
                  onClick={() => handleTabChange('expense')}
                >
                  Списания
                </Tab>
                <Tab
                  color="gray.500"
                  fontWeight="bold"
                  _selected={{ color: 'black', borderBottom: '2px solid black' }}
                  onClick={() => handleTabChange('income')}
                >
                  Поступления
                </Tab>
              </TabList>
            </Tabs>

            {/* DatePicker */}
            <HStack spacing={4}>
              <StyledDatePicker
                selected={startDate}
                onChange={(date: Date | null) => setStartDate(date)}
                selectsStart
                startDate={startDate || undefined}
                endDate={endDate || undefined}
                dateFormat="dd.MM.yyyy"
                placeholderText="Начало"
              />
              <StyledDatePicker
                selected={endDate}
                onChange={(date: Date | null) => setEndDate(date)}
                selectsEnd
                startDate={startDate || undefined}
                endDate={endDate || undefined}
                minDate={startDate || undefined}
                dateFormat="dd.MM.yyyy"
                placeholderText="Конец"
              />
            </HStack>
          </Flex>

          {/* Таблица операций */}
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Дата</Th>
                <Th>Операция</Th>
                <Th isNumeric>Количество</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedTransactions.map(transaction => (
                <Tr key={transaction.pk} height="92px">
                  <Td>{new Date().toLocaleDateString('ru-RU')}</Td>
                  <Td>
                    <Flex align="center">
                      <Icon
                        as={transaction.direction === 1 ? ArrowDownIcon : ArrowUpIcon}
                        color={transaction.direction === 1 ? 'green.500' : 'red.500'}
                        mr={2}
                      />
                      <Box>
                        <Text fontWeight="bold">Иван Иванов Н.</Text>
                        <Text fontSize="sm" color="gray.500">
                          {transaction.direction === 1 ? 'Поступление' : 'Списание'}
                        </Text>
                      </Box>
                    </Flex>
                  </Td>
                  <Td isNumeric color={transaction.direction === 1 ? 'green.500' : 'red.500'}>
                    {transaction.direction === 1 ? '+' : '-'}{' '}
                    {parseFloat(transaction.amount).toLocaleString()} ₽
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {/* Пагинация */}
          {totalPages > 1 && (
            <Flex justifyContent="center" mt={4}>
              <Button
                onClick={handlePreviousPage}
                disabled={page === 1}
                leftIcon={<ArrowBackIcon />}
                variant="link"
              >
                Предыдущая
              </Button>
              <Text mx={4}>Показать следующие {itemsPerPage}</Text>
              <Button
                onClick={handleNextPage}
                disabled={page === totalPages}
                rightIcon={<ArrowForwardIcon />}
                variant="link"
              >
                Следующая
              </Button>
            </Flex>
          )}
        </Box>

        {/* Блок баланса справа */}
        <Box p="32px" borderRadius="md" boxShadow="lg" width="304px" height="204px">
          <Text fontSize="md" color="#656575">
            Баланс
          </Text>
          <Box bgColor="gray.100" mt="7px" width="max-content" p={2} borderRadius={5}>
            <Text fontSize="sm" color="gray.500">
              Номер счета: {balance?.number || 'xxxx'}
            </Text>
          </Box>

          <Divider my={4} />

          <Text fontSize="2xl" fontWeight="bold" color="blackAlpha">
            ₽ {balance ? parseFloat(balance.total).toLocaleString() : 0}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};
